import { defineStore } from 'pinia';

/** Alert payload accepted by the `addAlert` action (see original JSDoc below). */
interface HubUIAlert {
  /** The text message to display in this alert */
  message: string;
  /** Alert type, e.g., 'success', 'error', 'warning', 'info' */
  type?: 'info' | 'error' | 'success' | 'warning';
  /** For how long this alert will be visible (ms). Minimum 1000ms, default 7000 */
  timeout?: number | null;
  /** Router link to wrap the alert in */
  to?: string | { name: string; params?: Record<string, string | number>; query?: Record<string, any> } | null;
  /** DEPRECATED (but still works): list of arbitrary scopes where the alert is displayed */
  scopes?: string[] | null;
  /** DEPRECATED (but still works): a single scope, merged with `scopes` */
  scope?: string | null;
}

/** Alert entry as stored in the snackbar stack */
interface HubUISnackbarItem {
  /** Unique alert id (timestamp + random suffix) */
  id: string;
  /** Visibility duration in ms (excl. fade-out) */
  duration: number;
  /** Alert type ('info' | 'error' | 'success' | 'warning') */
  type: HubUIAlert['type'];
  /** The displayed message */
  message: string;
  /** Optional router link the alert links to */
  to: HubUIAlert['to'];
  /** Scopes/pages where this alert should be shown; ['*'] = everywhere */
  scopes: string[];
  /** Auto-remove timer handle */
  timeout: ReturnType<typeof setTimeout> | null;
  /** Whether the alert is currently visible */
  visible: boolean;
}

/**
 * Defines the possible events that can trigger showing an armed NPS Survey
 */
export const NPSSurveyActionTrigger = {
  /**
   * User navigated to the "Dashboard" landing page
   */
  goToDashboard: 'goToDashboard',
  /**
   * User navigated to any other page on our dashboard
   */
  goToPage: 'goToPage',
} as const;

export type NPSSurveyActionTriggerType =
  (typeof NPSSurveyActionTrigger)[keyof typeof NPSSurveyActionTrigger];

/** State of the NPS survey prompt */
interface NpsSurveyState {
  /**
   * This controls whether we are going to show the survey
   * on the next eligible trigger, e.g. route navigation.
   */
  armed: boolean;
  /**
   * Whether to show the NPS Survey / it is open
   */
  open: boolean;
  /**
   * Internal.
   * setTimeout timer, if relevant
   */
  timer: ReturnType<typeof setTimeout> | null;
}

interface HubUiState {
  /** Whether the main navigation menu is open */
  isMenuOpen: boolean;
  /** Currently displayed/queued alerts */
  snackbarStack: HubUISnackbarItem[];
  /** NPS survey prompt state */
  npsSurvey: NpsSurveyState;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/hubUi.ts` (namespace `hubUi`).
 * In-memory only — no `vuexPersist` option (legacy `hubUi` was never listed in the `vuexLocal` /
 * `vuexSession` reducers in `src/store/index.ts`).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had BOTH a state prop `isMenuOpen` and a getter `isMenuOpen` that was a pure identity
 *   passthrough (`(state) => state.isMenuOpen`). Per the state-prop/getter collision rule, the
 *   state prop keeps the name `isMenuOpen` and the identity-passthrough getter is renamed
 *   `isMenuOpenGetter`.
 * - Legacy mutations `setValue` and `addAlert` had no matching actions of the same name in the
 *   legacy module (legacy `actions` was an empty object) — both become same-named actions, no
 *   rename needed for those two.
 */
export const useHubUiStore = defineStore('hubUi', {
  state: (): HubUiState => ({
    isMenuOpen: false,
    snackbarStack: [],
    npsSurvey: {
      armed: false,
      open: false,
      timer: null,
    },
  }),
  getters: {
    /**
     * Renamed from legacy getter `isMenuOpen` (collides with the state prop of the same name —
     * the state prop keeps the name; this identity-passthrough getter is renamed
     * `isMenuOpenGetter`).
     */
    isMenuOpenGetter(state): boolean {
      return state.isMenuOpen;
    },
    isTouchDevice(): boolean {
      return matchMedia('(hover: none)').matches;
    },
    /**
     * A function that will give you alerts for your desired scopes/namespace.
     * @callback AlertsInScope
     * @param {string[]} scopes If an alert has a "scope" that is contained within this array, it will be returned.
     * You can use a wildcard `["*"]` to get all alerts.
     * @returns {Array<Object>}
     * @example
     * const scopes = ["intents", "intent-view"];
     * const alerts = alertsInScope(scopes);
     * console.log(alerts); // [{message: "hi", scopes: ["intents","asd"], ...}]
     *
     * const alerts = alertsInScope(["*"]);
     * console.log(alerts.length); // 10
     *
     * // You could use this for basic route matching too:
     * const alerts = alertsInScope([this.$route.name]);
     * console.log(alerts); // [{message: "hi", scopes: ["intents","asd"], ...}]
     */
    /**
     * Return a function that will return all alerts within a given scope
     * @param state
     * @returns {AlertsInScope}
     */
    alertsInScope(state) {
      return (scope: string[]) => {
        const alerts: HubUISnackbarItem[] = [];
        const getAll = scope.includes('*');

        for (let i = 0; i < state.snackbarStack.length; i++) {
          // Passed scope contains "*"
          let addAlert = getAll;
          // Alert has no scope, thus scope "*"
          if (!addAlert && !state.snackbarStack[i].scopes?.length) addAlert = true;
          // Alert wants to use a scope "*", or...
          // Alert contains a scope matching one in the argument
          if (!addAlert && state.snackbarStack[i].scopes.some((s) => s === '*' || scope.includes(s))) addAlert = true;

          if (!addAlert) continue;

          alerts.push(state.snackbarStack[i]);
        }

        return alerts;
      };
    },
  },
  actions: {
    /**
     * Converted from legacy mutation `setValue` (no matching legacy action existed).
     * Generic key/value setter — key is dynamic, so index untyped.
     */
    setValue({ key, value }: { key: string; value: any }) {
      (this as any)[key] = value;
    },
    /**
     * @typedef {Object} HubUIAlert
     * @property {string} message The text message to display in this alert
     * @property {"info"|"error"|"success"|"warning"} [type="info"] Alert type, e.g., 'success', 'error', 'warning', 'info'
     * @property {?number} [timeout=7000] For how long this alert will be visible. Minimum 1000ms
     * @property {?string | ?{name: string, params?: Record<string, string|number>, query: Record<string, any>}} to Router link to wrap the alert in
     * @property {?string[]} [scopes=["*"]] DEPRECATED (but still works): A list of arbitrary scopes. Places where the alert will be displayed. If empty, displayed anywhere the component is used
     * @property {?string | "*"} [scope] DEPRECATED (but still works): A single scope. Automatically merged with `scopes` array if provided.
     * This is only used if you e.g. have a background worker and you only want the alerts it produce to be visible on specific pages, not on any page.
     */
    /**
     * Converted from legacy mutation `addAlert` (no matching legacy action existed).
     * Pushes an alert to the snackbar stack
     * @param {HubUIAlert} alert
     */
    addAlert(alert: HubUIAlert) {
      const time = alert.timeout ? Math.max(alert.timeout, 1000) : 7000;
      if (!alert.scopes) alert.scopes = alert.scope ? [] : ['*'];
      if (alert.scope && !alert.scopes.includes(alert.scope)) alert.scopes.push(alert.scope);

      const newItem: HubUISnackbarItem = {
        id: Date.now() + Math.random().toString(36).slice(2),
        duration: time,
        type: alert.type,
        message: alert.message,
        to: alert.to,
        scopes: alert.scopes || ['*'],
        timeout: null,
        visible: true,
      };

      // We must copy instead of mutate, for reactivity :(
      newItem.timeout = setTimeout(() => {
        this.snackbarStack = this.snackbarStack.filter((a) => a.id !== newItem.id);
      }, time + 300); // 300ms for the fade out animation

      // add the new item by assigning a new array so reactivity triggers
      this.snackbarStack = [...this.snackbarStack, newItem];
    },
    /**
     * Try to show the NPS survey in response to a navigation event.
     * Converted from the legacy vuex action of the same name.
     */
    tryNPSSurvey({ action }: { action: NPSSurveyActionTriggerType }) {
      /**
       * Don't exit early here if NPS survey is not armed,
       * because we might be in the middle of a race condition
       * between updating armed state and trying to execute it.
       *
       * The deferred `openNPSSurvey` handles the no-op if not armed.
       */

      this.clearNPSSurveyTimer();
      switch (action) {
        case NPSSurveyActionTrigger.goToDashboard:
          // Show 5 seconds after navigating to dashboard
          this.setNPSSurveyTimer(
            setTimeout(() => {
              this.openNPSSurvey();
            }, 5_000),
          );
          return;
        case NPSSurveyActionTrigger.goToPage:
          // Show immediately after navigation settles
          this.openNPSSurvey();
          return;
      }
    },
    /** Converted from legacy mutation `armNPSSurvey`. */
    armNPSSurvey(isArmed: boolean = true) {
      this.npsSurvey.armed = isArmed;
    },
    /**
     * Converted from legacy mutation `openNPSSurvey`.
     * No-ops unless the survey has been armed; opening also disarms it so it
     * can only be shown once per arming.
     */
    openNPSSurvey() {
      if (!this.npsSurvey.armed) return;

      this.npsSurvey.armed = false;
      this.npsSurvey.open = true;
    },
    /** Converted from legacy mutation `closeNPSSurvey`. */
    closeNPSSurvey() {
      this.npsSurvey.open = false;
    },
    /** Converted from legacy mutation `setNPSSurveyTimer`. */
    setNPSSurveyTimer(timer: ReturnType<typeof setTimeout> | null) {
      this.npsSurvey.timer = timer;
    },
    /** Converted from legacy mutation `clearNPSSurveyTimer`. */
    clearNPSSurveyTimer() {
      if (this.npsSurvey.timer) {
        clearTimeout(this.npsSurvey.timer);
        this.npsSurvey.timer = null;
      }
    },
  },
});
